/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { Fragment, useMemo, useState } from 'react';
import {
  DataTransferMappingStatus,
  IntegrationValueSource,
  SurveyDataStatus,
  type DataTransferSurveyResult,
} from '@g17eco/types/data-transfer';
import { getStatusBadgeClass, getStatusLabel, getSurveyDataStatusLabel, getSurveyDataStatusClass, getSurveyUrl, getSourceLabel, renderUnitInfo } from './data-transfer-utils';

interface QuestionGroup {
  key: string;
  utrCode: string;
  optionCode?: string;
  utrLabel: string;
  optionLabel?: string;
  valueType: string;
  unit?: string;
  numberScale?: string;
  surveyEntries: SurveyEntry[];
}

interface SurveyEntry {
  label: string;
  surveyId?: string;
  utrvId?: string;
  surveyValue?: string | number;
  surveyDataStatus: SurveyDataStatus;
  integrationValue?: string | number;
  valueSource?: IntegrationValueSource;
  hasDiscrepancy: boolean;
  status: DataTransferMappingStatus;
}

const buildQuestionGroups = (surveys: DataTransferSurveyResult[]): QuestionGroup[] => {
  // First pass: collect all unique keys and their metadata
  const groupMap = new Map<string, QuestionGroup>();
  const allKeys: string[] = [];

  for (const survey of surveys) {
    for (const row of survey.rows) {
      const key = row.optionCode ? `${row.utrCode}:${row.optionCode}` : row.utrCode;
      if (!groupMap.has(key)) {
        allKeys.push(key);
        groupMap.set(key, {
          key,
          utrCode: row.utrCode,
          optionCode: row.optionCode,
          utrLabel: row.utrLabel,
          optionLabel: row.optionLabel,
          valueType: row.valueType,
          unit: row.survey.unit,
          numberScale: row.survey.numberScale,
          surveyEntries: [],
        });
      }
    }
  }

  // Second pass: for each survey, build a lookup and ensure every group gets an entry
  for (const survey of surveys) {
    const rowByKey = new Map<string, DataTransferSurveyResult['rows'][number]>();
    for (const row of survey.rows) {
      const key = row.optionCode ? `${row.utrCode}:${row.optionCode}` : row.utrCode;
      rowByKey.set(key, row);
    }

    for (const key of allKeys) {
      const group = groupMap.get(key);
      if (!group) {
        continue;
      }
      const row = rowByKey.get(key);
      if (row) {
        group.surveyEntries.push({
          label: survey.label,
          surveyId: row.survey.surveyId ?? survey.surveyId,
          utrvId: row.survey.utrvId,
          surveyValue: row.survey.value,
          surveyDataStatus: row.survey.status,
          integrationValue: row.integration.value,
          valueSource: row.integration.source,
          hasDiscrepancy: row.hasDiscrepancy,
          status: row.status,
        });
      } else {
        group.surveyEntries.push({
          label: survey.label,
          surveyId: survey.surveyId,
          surveyDataStatus: SurveyDataStatus.NotAnswered,
          hasDiscrepancy: false,
          status: group.surveyEntries[0]?.status ?? DataTransferMappingStatus.Pending,
        });
      }
    }
  }

  return allKeys.map((key) => groupMap.get(key)).filter((g): g is QuestionGroup => g !== undefined);
};


const renderSourceLabel = (source?: IntegrationValueSource) => {
  const label = getSourceLabel(source);
  if (!label) {
    return null;
  }
  return <span className='small text-ThemeTextLight ms-1'>({label})</span>;
};

const getCellClass = (entry: SurveyEntry): string => {
  if (entry.hasDiscrepancy) {
    return 'table-warning';
  }
  return '';
};


const renderSurveyValue = (entry: SurveyEntry, childInitiativeId: string) => {
  const url = getSurveyUrl(entry, childInitiativeId);

  if (entry.surveyDataStatus === SurveyDataStatus.Value) {
    if (url) {
      return (
        <a href={url} target='_blank' rel='noopener noreferrer' className='text-decoration-none'>
          {entry.surveyValue}
        </a>
      );
    }
    return <>{entry.surveyValue}</>;
  }

  const label = getSurveyDataStatusLabel(entry.surveyDataStatus);
  if (url) {
    return (
      <a href={url} target='_blank' rel='noopener noreferrer' className={getSurveyDataStatusClass(entry.surveyDataStatus)}>
        {label} <i className='fa fa-external-link small' />
      </a>
    );
  }
  return <span className={getSurveyDataStatusClass(entry.surveyDataStatus)}>{label}</span>;
};

interface DataTransferQuestionViewProps {
  surveys: DataTransferSurveyResult[];
  filteredSurveys: DataTransferSurveyResult[];
  childInitiativeId: string;
}

export const DataTransferQuestionView = ({ surveys, filteredSurveys, childInitiativeId }: DataTransferQuestionViewProps) => {
  // Build groups from unfiltered surveys for proper column alignment
  const groups = useMemo(() => buildQuestionGroups(surveys), [surveys]);

  // Build a set of keys that pass the filter to determine which groups to show
  const filteredKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const survey of filteredSurveys) {
      for (const row of survey.rows) {
        keys.add(row.optionCode ? `${row.utrCode}:${row.optionCode}` : row.utrCode);
      }
    }
    return keys;
  }, [filteredSurveys]);

  const totalRows = surveys.reduce((acc, s) => acc + s.rows.length, 0);
  const filteredRowCount = filteredSurveys.reduce((acc, s) => acc + s.rows.length, 0);
  const hasFilters = filteredRowCount < totalRows;
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const surveyLabels = useMemo(() => surveys.map((s) => s.label), [surveys]);

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Group by UTR code for collapsible multi-option UTRs, filtered to visible keys
  const utrGroups = useMemo(() => {
    const visibleGroups = hasFilters ? groups.filter((g) => filteredKeys.has(g.key)) : groups;
    const map = new Map<string, QuestionGroup[]>();
    for (const group of visibleGroups) {
      const existing = map.get(group.utrCode);
      if (existing) {
        existing.push(group);
      } else {
        map.set(group.utrCode, [group]);
      }
    }
    return Array.from(map.entries());
  }, [groups, hasFilters, filteredKeys]);

  return (
    <div className='table-responsive'>
      <table className='table table-bordered table-sm align-middle'>
        <thead>
          <tr>
            <th style={{ width: '30px' }} />
            <th>Metric</th>
            {surveyLabels.map((label) => (
              <Fragment key={label}>
                <th>{label} Survey</th>
                <th>{label} Integration</th>
              </Fragment>
            ))}
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {utrGroups.map(([utrCode, questionGroups]) => {
            if (questionGroups.length === 1) {
              const group = questionGroups[0];
              return (
                <tr key={group.key}>
                  <td style={{ width: '30px' }} />
                  <td>
                    <code className='small'>{group.utrCode}</code>
                    <span className='small text-ThemeTextLight ms-2'>{group.valueType}</span>
                    {renderUnitInfo(group.unit, group.numberScale)}
                    <div>{group.utrLabel}</div>
                    {group.optionCode && (
                      <div className='small text-ThemeTextLight'>
                        <i className='fa fa-columns me-1' />
                        {group.optionLabel ?? group.optionCode}
                      </div>
                    )}
                  </td>
                  {group.surveyEntries.map((entry) => (
                    <Fragment key={entry.label}>
                      <td className={getCellClass(entry)}>
                        {renderSurveyValue(entry, childInitiativeId)}
                      </td>
                      <td className={getCellClass(entry)}>
                        {entry.integrationValue ?? <span className='text-ThemeTextLight'>—</span>}
                        {renderSourceLabel(entry.valueSource)}
                      </td>
                    </Fragment>
                  ))}
                  <td>
                    <span className={`badge ${getStatusBadgeClass(group.surveyEntries[0].status)}`}>
                      {getStatusLabel(group.surveyEntries[0].status)}
                    </span>
                  </td>
                </tr>
              );
            }

            const isExpanded = expandedGroups.has(utrCode);
            const firstGroup = questionGroups[0];
            const hasDiscrepancy = questionGroups.some((g) => g.surveyEntries.some((e) => e.hasDiscrepancy));
            const rowClass = hasDiscrepancy ? 'table-warning' : '';

            return (
              <Fragment key={utrCode}>
                <tr className={`cursor-pointer ${rowClass}`} onClick={() => toggleGroup(utrCode)}>
                  <td className='text-center' style={{ width: '30px' }}>
                    <i className={`fa fa-chevron-${isExpanded ? 'down' : 'right'} text-ThemeTextLight`} />
                  </td>
                  <td>
                    <code className='small'>{utrCode}</code>
                    <span className='small text-ThemeTextLight ms-2'>{firstGroup.valueType}</span>
                    {renderUnitInfo(firstGroup.unit, firstGroup.numberScale)}
                    <div className='fw-bold'>{firstGroup.utrLabel}</div>
                    <span className='badge background-ThemeIconSecondary text-ThemeTextWhite'>
                      {questionGroups.length} options
                    </span>
                  </td>
                  {surveyLabels.map((label, i) => {
                    const withValue = questionGroups.filter(
                      (g) => g.surveyEntries[i]?.surveyDataStatus === SurveyDataStatus.Value,
                    ).length;
                    return (
                      <Fragment key={label}>
                        <td>
                          <span className={withValue === questionGroups.length ? 'text-ThemeSuccessMedium' : 'text-ThemeWarningMedium'}>
                            {withValue}/{questionGroups.length}
                          </span>
                        </td>
                        <td><span className='text-ThemeTextLight'>—</span></td>
                      </Fragment>
                    );
                  })}
                  <td>
                    <span className={`badge ${getStatusBadgeClass(firstGroup.surveyEntries[0].status)}`}>
                      {getStatusLabel(firstGroup.surveyEntries[0].status)}
                    </span>
                  </td>
                </tr>
                {isExpanded && questionGroups.map((g) => (
                  <tr key={g.key}>
                    <td style={{ width: '30px' }} />
                    <td className='ps-4'>
                      <code className='small'>{g.optionCode}</code>
                      {g.optionLabel && <span className='ms-2 small'>{g.optionLabel}</span>}
                      {renderUnitInfo(g.unit, g.numberScale)}
                    </td>
                    {g.surveyEntries.map((entry) => (
                      <Fragment key={entry.label}>
                        <td className={getCellClass(entry)}>
                          {renderSurveyValue(entry, childInitiativeId)}
                        </td>
                        <td className={getCellClass(entry)}>
                          {entry.integrationValue ?? <span className='text-ThemeTextLight'>—</span>}
                          {getSourceLabel(entry.valueSource) && (
                            <span className='small text-ThemeTextLight ms-1'>({getSourceLabel(entry.valueSource)})</span>
                          )}
                        </td>
                      </Fragment>
                    ))}
                    <td>
                      <span className={`badge ${getStatusBadgeClass(g.surveyEntries[0].status)}`}>
                        {getStatusLabel(g.surveyEntries[0].status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
